/**
 * React Query (TanStack Query) Configuration
 *
 * 서버 상태 관리, 캐싱, 백그라운드 리페치, 낙관적 업데이트.
 *
 * @see https://tanstack.com/query/latest
 */

import { QueryClient } from '@tanstack/react-query';

// ============================================================================
// QUERY CLIENT
// ============================================================================

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: 5분 (이 시간 동안은 캐시 데이터 사용)
      staleTime: 5 * 60 * 1000,

      // Cache time: 1시간 (ISSUE_037: 무한 스크롤용 장기 캐시)
      gcTime: 60 * 60 * 1000,

      // Retry: 1회만 재시도 (ISSUE_037: 빠른 실패)
      retry: 1,
      retryDelay: 1000,

      // Refetch on window focus: 비활성화 (ISSUE_037: 불필요한 리페치 방지)
      refetchOnWindowFocus: false,

      // Refetch on reconnect: 네트워크 복구 시 자동 리페치
      refetchOnReconnect: true,

      // Refetch on mount: 컴포넌트 마운트 시 stale이면 리페치
      refetchOnMount: true,
    },
    mutations: {
      // Retry: mutation은 기본적으로 재시도 안 함
      retry: false,
    },
  },
});

// ============================================================================
// QUERY KEYS FACTORY
// ============================================================================

/**
 * Query Keys Factory
 * 일관된 쿼리 키 생성을 위한 팩토리 함수들.
 *
 * @example
 * ```ts
 * // 학생 목록
 * queryKey: queryKeys.students.list(academyId)
 *
 * // 특정 학생
 * queryKey: queryKeys.students.detail(studentId)
 *
 * // 세션 목록 (날짜 범위)
 * queryKey: queryKeys.sessions.range(academyId, startDate, endDate)
 * ```
 */
export const queryKeys = {
  // Academy
  academy: {
    all: ['academy'] as const,
    detail: (id: string) => ['academy', id] as const,
    settings: (id: string) => ['academy', id, 'settings'] as const,
  },

  // Students — ISSUE_167: 단순캐시. workspaceKey = academyId | `scratch-{ownerId}`
  students: {
    all: ['students'] as const,
    list: (workspaceKey: string) => ['students', 'list', workspaceKey] as const,
    detail: (studentId: string) => ['students', 'detail', studentId] as const,
    search: (workspaceKey: string, query: string) =>
      ['students', 'search', workspaceKey, query] as const,
  },

  // Schools — ISSUE_167: 단순캐시(강캐시·정적). workspaceKey = academyId | `scratch-{ownerId}`
  schools: {
    all: ['schools'] as const,
    list: (workspaceKey: string) => ['schools', 'list', workspaceKey] as const,
  },

  // School materials — ISSUE_176-B: 학교 자료 탭. dual-workspace + schoolId 종속.
  // key = `${workspaceKey}:${schoolId}` (학교마다 별도 캐시)
  schoolMaterials: {
    all: ['schoolMaterials'] as const,
    list: (key: string) => ['schoolMaterials', 'list', key] as const,
  },

  // Classrooms — ISSUE_167: 단순캐시(중캐시·학원설정·realtime invalidate). academy 전용
  classrooms: {
    all: ['classrooms'] as const,
    list: (academyId: string) => ['classrooms', 'list', academyId] as const,
  },

  // Branches — ISSUE_167: 단순캐시(중캐시·학원설정·realtime invalidate). academy 전용
  branches: {
    all: ['branches'] as const,
    list: (academyId: string) => ['branches', 'list', academyId] as const,
  },

  // Teachers
  teachers: {
    all: ['teachers'] as const,
    list: (academyId: string) => ['teachers', 'list', academyId] as const,
    detail: (teacherId: string) => ['teachers', 'detail', teacherId] as const,
    capabilities: (teacherId: string) =>
      ['teachers', 'capabilities', teacherId] as const,
  },

  // Question Sets — ISSUE_167: 단순캐시(듀얼ws: academyId | `scratch-{userId}`)
  questionSets: {
    all: ['questionSets'] as const,
    list: (workspaceKey: string) => ['questionSets', 'list', workspaceKey] as const,
  },

  // Sessions
  sessions: {
    all: ['sessions'] as const,
    list: (academyId: string) => ['sessions', 'list', academyId] as const,
    /** @deprecated Use week() instead - range keys cause cache fragmentation */
    range: (academyId: string, startDate: string, endDate: string) =>
      ['sessions', 'range', academyId, startDate, endDate] as const,
    /**
     * Week bucket key (ISSUE_037)
     * @param workspaceId - Academy ID or 'scratch'
     * @param weekStartStr - Monday date in yyyy-MM-dd format
     */
    week: (workspaceId: string, weekStartStr: string) =>
      ['sessions', workspaceId, 'week', weekStartStr] as const,
    detail: (sessionId: string) => ['sessions', 'detail', sessionId] as const,
    byTeacher: (teacherId: string, date: string) =>
      ['sessions', 'teacher', teacherId, date] as const,
  },

  // Session Types
  sessionTypes: {
    all: ['sessionTypes'] as const,
    list: (academyId: string) => ['sessionTypes', 'list', academyId] as const,
  },

  // Courses
  courses: {
    all: ['courses'] as const,
    list: (academyId: string) => ['courses', 'list', academyId] as const,
    detail: (courseId: string) => ['courses', 'detail', courseId] as const,
  },
} as const;

// ============================================================================
// INVALIDATION HELPERS
// ============================================================================

/**
 * Invalidate all queries for an entity type
 */
export function invalidateEntity(entity: keyof typeof queryKeys) {
  return queryClient.invalidateQueries({
    queryKey: queryKeys[entity].all,
  });
}

/**
 * Prefetch query (for route transitions)
 */
export async function prefetchQuery<T>(
  queryKey: readonly unknown[],
  queryFn: () => Promise<T>
) {
  await queryClient.prefetchQuery({
    queryKey,
    queryFn,
    staleTime: 5 * 60 * 1000,
  });
}
